import type { ReactNode } from 'react';

import {
	type ActionMeta,
	type ControlProps as AKControlProps,
	type MenuListComponentProps as AKMenuListComponentProps,
	type OptionProps as AKOptionProps,
	type GroupType,
	type OptionType,
	type SelectProps,
} from '@atlaskit/select';

interface CustomType {
	optionType: 'option' | 'avatar' | 'lozenge' | 'group' | 'error';
}

interface OptionBase extends OptionType, CustomType {
	aboveTheFold?: boolean;
	cursor?: string;
	data?: Option;
	icon?: string;
	invalid?: boolean;
	value: string;
}

export interface Option extends OptionBase {
	optionType: 'option';
}

export interface AvatarOption extends OptionBase {
	avatar?: string | null;
	hideAvatar?: boolean;
	optionType: 'avatar';
	square?: boolean;
}

export interface LozengeOption extends OptionBase {
	appearance?: 'default' | 'inprogress' | 'moved' | 'new' | 'removed' | 'success';
	isBold?: boolean;
	maxWidth?: number | string;
	optionType: 'lozenge';
}

export type SingleOption = Option | AvatarOption | LozengeOption;

export type SelectValues = readonly SingleOption[];

export interface OptionGroup extends GroupType<SingleOption>, CustomType {
	optionType: 'group';
}

export type SelectOption = SingleOption | OptionGroup;

export type SelectOptions = readonly SelectOption[];

export interface ErrorOption extends CustomType {
	error: true | Error;
	onRetry: () => void;
	optionType: 'error';
}

export type SelectOrErrorOptions = readonly (SelectOption | ErrorOption)[];

export type SearchValue = string;

export type FilterValue = {
	label: string;
	value: string;
}[];

export interface FieldProps {
	isRemovable?: boolean;
	key?: string | null;
	label: string;
	type: string;
}

export type LoadOptions = (
	inputValue: string,
	cb: (a: SelectValues) => void,
) => void | Promise<void>;

export interface PaginationConfig {
	enableFullList: boolean;
	keywordValues?: string[];
	showKeywordValuesInFullList?: boolean;
}

export interface AsyncLoadOptionsData {
	options: SelectOrErrorOptions;
	totalCount: number | null;
}

export type AsyncLoadOptions = (
	search: string,
	enableFullList: boolean,
	showFullList: boolean,
	showMore: boolean,
	lastOption: SelectOption | undefined,
	cb: (data: AsyncLoadOptionsData) => void,
) => void;

export interface PaginatedAsyncSelectField extends FieldProps {
	cacheOptions: boolean;
	defaultOptions: SelectOption[] | boolean;
	defaultOptionsLabel?: string;
	enableShowMore?: boolean;
	inputValue?: string;
	invalidLabel?: string;
	loadOptions?: AsyncLoadOptions;
	onInputChange?: () => void;
	onMenuScrollToBottom?: () => void;
	onMenuScrollToTop?: () => void;
	pagination?: PaginationConfig;
	placeholder?: ReactNode;
}

export const MetaActions = {
	setValue: 'set-value',
	inputChange: 'input-change',
	inputBlur: 'input-blur',
	menuClose: 'menu-close',
	selectOption: 'select-option',
	deselectOption: 'deselect-option',
	removeValue: 'remove-value',
	popValue: 'pop-value',
	clear: 'clear',
	createOption: 'create-option',
	clearOptions: 'clear-options',
	add: 'add',
	remove: 'remove',
	update: 'update',
};

export interface FilterOption {
	data: SingleOption;
	label: string;
	value: string;
}

export type FormatOptionLabel = (option: SelectOption) => ReactNode;

export type OnChangeWithData = (
	options: SelectValues,
	option: ActionMeta<SelectOption>,
	selectData?: {
		selectedIndex: number;
	},
) => void;

export interface BaseSelectProps extends SelectProps<SingleOption, true> {
	ariaLabel?: string;
	cacheOptions: boolean;
	defaultOptions: SelectOption[] | boolean;
	defaultOptionsLabel?: string;
	fieldInvalidLabel?: string | null;
	fieldKey?: string | null;
	fieldLabel?: string | null;
	loadOptions: LoadOptions;
	menuListProps: {
		error: {
			error: Error | null;
			isError: boolean;
			onRetry: (() => void) | null;
		};
		handleShowMore: () => void;
		isLoading: boolean;
		isPaginationLoading: boolean;
		showShowMoreButton: boolean;
		type?: string | null;
	};
	onClear?: () => void;
	onFilterOption?: (option: FilterOption, rawInput: string) => boolean;
	options?: SelectOptions;
}

export interface ProxyBaseSelectProps extends BaseSelectProps {
	selectOptions?: SelectOptions;
}

export type ControlProps = AKControlProps<SingleOption, true>;
export type OptionProps = AKOptionProps<OptionType, true>;
export type MenuListComponentProps = AKMenuListComponentProps<SingleOption, true>;
